import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ErrorIcon from '@mui/icons-material/Error';
import {
  getItemDescriptionsAndPricesTypography,
  getHeaderAccentMain,
  getMenuItemNamesLighterColor,
  getSectionNavMediumColor,
  getPageBackgroundColor,
  getSectionNavLightColor
} from 'styles/helpers/getThemeStyles';

const StyledTextField = styled(TextField)(({ theme }) => {
  let inputStyles = {};
  let labelStyles = {};
  const fontStyles = getItemDescriptionsAndPricesTypography(theme);
  inputStyles = {
    color: getSectionNavMediumColor(theme),
    ...fontStyles
  };
  labelStyles = {
    backgroundColor: `${getPageBackgroundColor(theme)} !important`,
    border: `1px solid ${getMenuItemNamesLighterColor(theme)}`,
    ...fontStyles
  };

  return {
    '& .MuiFilledInput-root': {
      ...labelStyles,
      '&.Mui-focused': {
        boxShadow: `inset 0 -10px 0 -8px ${getHeaderAccentMain(theme)}`,
        transition: 'box-shadow 100ms'
      }
    },
    '& .MuiInputLabel-root': {
      ...inputStyles,
      '&.Mui-focused:not(&.Mui-error)': {
        color: getHeaderAccentMain(theme)
      },
      '&.Mui-error': {
        color: theme.palette.error.main
      }
    },
    '& .MuiFilledInput-input': {
      ...inputStyles
    }
  };
});

function AddressInput({
  error,
  placeholder,
  ariaAutocomplete,
  includeAdornment,
  isOnDarkBackground,
  'data-testid': dataTestId,
  ...textFieldProps
}) {
  // if on dark background, we don't want a border.
  // the border creates a gap between the mui error border
  // https://share.getbento.com/7KuR1BBg
  const filledInputStyles = isOnDarkBackground ? { border: 0 } : {};
  const EndAdornment = !error ? LocationOnIcon : ErrorIcon;
  const getAdornmentSx = theme => ({
    fill: error ? theme.palette.error.main : getSectionNavLightColor(theme)
  });
  return (
    <StyledTextField
      {...textFieldProps}
      variant="filled"
      fullWidth
      inputProps={{
        'data-testid': dataTestId,
        autoComplete: 'street-address',
        'aria-autocomplete': ariaAutocomplete
      }}
      placeholder={placeholder}
      error={!!error}
      InputProps={{
        disableUnderline: !error,
        style: filledInputStyles,
        endAdornment: includeAdornment && (
          <InputAdornment position="end">
            <EndAdornment sx={theme => getAdornmentSx(theme)} />
          </InputAdornment>
        )
      }}
    />
  );
}

AddressInput.propTypes = {
  error: PropTypes.string,
  placeholder: PropTypes.string,
  ariaAutocomplete: PropTypes.string,
  includeAdornment: PropTypes.bool,
  isOnDarkBackground: PropTypes.bool,
  'data-testid': PropTypes.string
};

AddressInput.defaultProps = {
  error: '',
  placeholder: '',
  ariaAutocomplete: '',
  includeAdornment: false,
  isOnDarkBackground: false,
  'data-testid': 'address-input'
};

export default AddressInput;
